import type { Beast, BeastState } from '../../../models/Beast';
import type { Being } from '../../../models/Being';
import type { Surroundings } from '../../../models/Surroundings';
import type { IBeastState } from './IBeastState';
import { BEAST } from '../../../models/constants';
import { BeastFleeingState } from './BeastFleeingState';
import { DeadState } from '../being/DeadState';
import type { PubSub } from '../../../PubSub';
import type { GameEvents } from '../../../events';

/**
 * Interrupt state: an angered Beast kills a Being that has closed inside
 * `BEAST.GORE_RADIUS_SQ`.
 *
 * Registered ahead of `BeastFleeingState` so a cornered Beast turns on its pursuers
 * rather than continuing to run. This is what stops a lone Being from chasing a Beast
 * around a small island indefinitely: flight is the Beast's first answer, but once it
 * has been wounded and can no longer open the distance, proximity is fatal. The pack
 * still wins by cornering — it simply pays for it.
 */
export class BeastGoringState implements IBeastState {
    static readonly stateName = 'Beast Goring';

    private readonly pubsub: PubSub<GameEvents>;

    /**
     * @param pubsub - Event bus used to publish `world:being-gored` so the view can
     *   play the kill effect at the victim's position.
     */
    constructor({ pubsub }: { pubsub: PubSub<GameEvents> }) {
        this.pubsub = pubsub;
    }

    get allowInterrupt(): boolean {
        return true;
    }

    canEnter(beast: Beast, surroundings: Surroundings): boolean {
        if (!beast.isAngered) return false;
        return this.nearestVictim(beast, surroundings) !== null;
    }

    /**
     * Gores one Being per tick — the closest — rather than everything in reach, so a
     * surrounded Beast culls the pack a body at a time instead of wiping it in a
     * single step. The Beast forfeits its movement this tick to do it.
     */
    tick(beast: Beast, surroundings: Surroundings): BeastState {
        const victim = this.nearestVictim(beast, surroundings);
        if (!victim) return BeastFleeingState.stateName;

        victim.setHealth(0);
        victim.state = DeadState.stateName;
        this.pubsub.publish('world:being-gored', { x: victim.x, z: victim.z });

        return BeastFleeingState.stateName;
    }

    /**
     * Returns the closest living Being within goring reach, or null if none. Dead
     * Beings are skipped: they are never removed from the world, so without this the
     * Beast would spend its turns goring corpses.
     */
    private nearestVictim(beast: Beast, surroundings: Surroundings): Being | null {
        let nearest: Being | null = null;
        let nearestSq = BEAST.GORE_RADIUS_SQ;

        for (const being of surroundings.nearbyBeings) {
            if (being.state === DeadState.stateName) continue;
            if (being.health <= 0) continue;

            const dx = being.x - beast.x;
            const dz = being.z - beast.z;
            const distSq = dx * dx + dz * dz;
            if (distSq <= nearestSq) {
                nearestSq = distSq;
                nearest = being;
            }
        }

        return nearest;
    }
}
